import React, { useEffect, useRef, useState } from 'react';

/**
 * Ethereal / Majestic / Atmospheric Loading Display — V2 (TypeScript-safe)
 * - Same visuals as the last version (aurora, starfield, bokeh, comets, sigil rings)
 * - Fixes TS strict-null checks by hard-narrowing the 2D context
 * - Zero external assets. Respects prefers-reduced-motion.
 */

const BirthdayLoadingDisplay = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const reduceMotionRef = useRef<boolean>(false);

  const [loadingTextIndex, setLoadingTextIndex] = useState(0);

  const loadingTexts = [
    'Weaving starlight…',
    'Summoning auroras…',
    'Aligning constellations…',
    'Awakening the lumen engine…',
    'Whispering to pixels…',
    'Shaping the atmosphere…',
  ];

  // Cycle text
  useEffect(() => {
    const i = window.setInterval(() => {
      setLoadingTextIndex((p) => (p + 1) % loadingTexts.length);
    }, 2000);
    return () => window.clearInterval(i);
  }, []);

  // Starfield + bokeh + comets + soft constellations
  useEffect(() => {
    const prefersReduced =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    reduceMotionRef.current = prefersReduced;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d', { alpha: true });
    if (!context) return; // Bail if the context isn't available (very rare)
    // From here down, use strongly typed non-null ctx so TS won't complain.
    const ctx: CanvasRenderingContext2D = context;

    let width = 0;
    let height = 0;
    let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1)); // cap DPR for perf
    let lastTs = 0;

    type Star = {
      x: number;
      y: number;
      r: number;
      baseAlpha: number;
      twinkleAmp: number;
      twinkleSpeed: number;
      twinklePhase: number;
      driftX: number;
      driftY: number;
      layer: number; // 0 far, 1 mid, 2 near
      hueShift: number;
    };

    type Bokeh = {
      x: number;
      y: number;
      r: number;
      alpha: number;
      vx: number;
      vy: number;
    };

    type Comet = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      maxLife: number;
      size: number;
    };

    let stars: Star[] = [];
    let bokehs: Bokeh[] = [];
    let comets: Comet[] = [];

    const rand = (min: number, max: number) => min + Math.random() * (max - min);

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      buildScene();
    };

    const starTarget = () => {
      const area = (width * height) / 1400;
      return Math.max(160, Math.min(700, Math.floor(area)));
    };

    function buildScene() {
      // Stars (layered)
      const total = starTarget();
      stars = [];
      for (let i = 0; i < total; i++) {
        const layer = Math.random() < 0.6 ? 0 : Math.random() < 0.8 ? 1 : 2;
        const sizeBase = layer === 0 ? 0.4 : layer === 1 ? 0.8 : 1.2;
        const twinkleBase = layer === 0 ? 0.05 : layer === 1 ? 0.12 : 0.2;

        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: rand(sizeBase, sizeBase + 0.8),
          baseAlpha: rand(0.22, 0.72),
          twinkleAmp: rand(twinkleBase * 0.6, twinkleBase * 1.4),
          twinkleSpeed: rand(0.5, 2.0),
          twinklePhase: Math.random() * Math.PI * 2,
          driftX: reduceMotionRef.current ? 0 : rand(-0.02, 0.04) * (1 + layer * 0.3),
          driftY: reduceMotionRef.current ? 0 : rand(-0.02, 0.04) * (1 + layer * 0.3),
          layer,
          hueShift: rand(-10, 18),
        });
      }

      // Bokeh glows — sparse, oversized, cinematic
      const bokehCount = Math.max(5, Math.min(18, Math.floor((width * height) / 120000)));
      bokehs = [];
      for (let i = 0; i < bokehCount; i++) {
        const r = rand(40, Math.min(160, Math.max(80, Math.sqrt(width * height) * 0.07)));
        bokehs.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r,
          alpha: rand(0.04, 0.12),
          vx: reduceMotionRef.current ? 0 : rand(-0.03, 0.03),
          vy: reduceMotionRef.current ? 0 : rand(-0.02, 0.02),
        });
      }

      // Reset comets on rebuild
      comets = [];
    }

    const spawnComet = () => {
      if (reduceMotionRef.current) return;
      // launch from a random edge
      const edge = Math.floor(Math.random() * 4);
      let x = 0, y = 0, vx = 0, vy = 0;
      const speed = rand(0.9, 1.7);
      switch (edge) {
        case 0: // top -> down-right
          x = rand(-width * 0.1, width * 0.3);
          y = -40;
          vx = speed; vy = speed * rand(0.6, 1.2);
          break;
        case 1: // right -> left-down
          x = width + 40;
          y = rand(-height * 0.1, height * 0.3);
          vx = -speed * rand(0.8, 1.4); vy = speed * rand(0.3, 0.9);
          break;
        case 2: // bottom -> up-left
          x = rand(width * 0.7, width * 1.1);
          y = height + 40;
          vx = -speed; vy = -speed * rand(0.6, 1.2);
          break;
        default: // left -> right-up
          x = -40;
          y = rand(height * 0.7, height * 1.1);
          vx = speed * rand(0.8, 1.4); vy = -speed * rand(0.3, 0.9);
      }
      comets.push({ x, y, vx, vy, life: 0, maxLife: rand(1200, 2200), size: rand(1.4, 2.2) });
    };

    // Soft constellation lines
    function drawConstellations(ctx2: CanvasRenderingContext2D, ts: number) {
      if (stars.length === 0) return;
      const candidates = stars.filter((s) => s.layer >= 1);
      const maxSegments = Math.min(24, Math.floor(candidates.length * 0.04));
      let drawn = 0;

      for (let i = 0; i < candidates.length && drawn < maxSegments; i++) {
        const a = candidates[i];
        const neighborIndex =
          (i + Math.floor((Math.sin(ts * 0.0003 + i) + 1) * 3) + 1) % candidates.length;
        const b = candidates[neighborIndex];

        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy);

        if (dist < Math.min(120, Math.max(60, Math.sqrt(width * height) * 0.06))) {
          const alpha = 0.08 + 0.06 * Math.sin((ts * 0.001 + i) * 0.6);
          ctx2.strokeStyle = `rgba(200,225,255,${alpha})`;
          ctx2.lineWidth = 0.6;
          ctx2.beginPath();
          ctx2.moveTo(a.x, a.y);
          ctx2.lineTo(b.x, b.y);
          ctx2.stroke();
          drawn++;
        }
      }
    }

    const draw = (ts: number) => {
      rafRef.current = requestAnimationFrame(draw);
      const dt = lastTs ? Math.min(32, ts - lastTs) : 16;
      lastTs = ts;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Transparent fade clears to keep trails subtle
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(3,7,18,0.20)';
      ctx.fillRect(0, 0, width, height);

      // Bokeh (behind stars)
      for (let b of bokehs) {
        b.x += b.vx * dt * 0.06;
        b.y += b.vy * dt * 0.06;

        if (b.x < -b.r) b.x = width + b.r;
        if (b.x > width + b.r) b.x = -b.r;
        if (b.y < -b.r) b.y = height + b.r;
        if (b.y > height + b.r) b.y = -b.r;

        const grad = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        grad.addColorStop(0, `rgba(200,220,255,${b.alpha})`);
        grad.addColorStop(0.6, `rgba(140,180,255,${b.alpha * 0.28})`);
        grad.addColorStop(1, 'rgba(100,140,220,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Stars
      for (let s of stars) {
        s.x += s.driftX * dt * 0.06;
        s.y += s.driftY * dt * 0.06;

        if (s.x < -10) s.x = width + 10;
        if (s.x > width + 10) s.x = -10;
        if (s.y < -10) s.y = height + 10;
        if (s.y > height + 10) s.y = -10;

        const alpha =
          s.baseAlpha +
          Math.sin(ts * 0.001 * s.twinkleSpeed + s.twinklePhase) * s.twinkleAmp;

        ctx.shadowBlur = 12;
        ctx.shadowColor = 'rgba(170,210,255,0.75)';
        ctx.fillStyle = `rgba(235,245,255,${alpha})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();

        if (!reduceMotionRef.current && s.layer >= 1 && Math.random() < 0.0015) {
          // Momentary sparkle
          ctx.strokeStyle = 'rgba(230,245,255,0.75)';
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(s.x - 3, s.y);
          ctx.lineTo(s.x + 3, s.y);
          ctx.moveTo(s.x, s.y - 3);
          ctx.lineTo(s.x, s.y + 3);
          ctx.stroke();
        }
      }

      // Constellations
      drawConstellations(ctx, ts);

      // Maybe spawn comets
      if (!reduceMotionRef.current && Math.random() < 0.02) spawnComet();

      // Comets
      for (let i = comets.length - 1; i >= 0; i--) {
        const c = comets[i];
        c.life += dt;
        c.x += c.vx * dt * 0.06;
        c.y += c.vy * dt * 0.06;

        const t = c.life / c.maxLife;
        if (t >= 1 || c.x < -80 || c.x > width + 80 || c.y < -80 || c.y > height + 80) {
          comets.splice(i, 1);
          continue;
        }

        // comet head
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.size + (1 - t) * 1.5, 0, Math.PI * 2);
        ctx.closePath();
        ctx.shadowBlur = 20;
        ctx.shadowColor = 'rgba(160,200,255,0.9)';
        ctx.fillStyle = 'rgba(220,240,255,0.95)';
        ctx.fill();

        // trail
        const trailX = c.x - c.vx * 4;
        const trailY = c.y - c.vy * 4;
        const grad = ctx.createLinearGradient(trailX, trailY, c.x, c.y);
        grad.addColorStop(0, 'rgba(130,180,255,0)');
        grad.addColorStop(1, 'rgba(210,235,255,0.85)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = Math.max(1, c.size * 0.9);
        ctx.beginPath();
        ctx.moveTo(trailX - c.vx * 4, trailY - c.vy * 4);
        ctx.lineTo(c.x, c.y);
        ctx.stroke();
      }
    };

    resize();
    window.addEventListener('resize', resize);

    // Prime canvas
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, width, height);

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        /* ---------- Global Animations ---------- */
        @keyframes aurora-shift {
          0% { transform: translate(-10%, -10%) rotate(0deg); }
          50% { transform: translate(10%, 5%) rotate(180deg); }
          100% { transform: translate(-10%, -10%) rotate(360deg); }
        }
        @keyframes float-slow { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
        @keyframes rotate-cw { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes rotate-ccw { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
        @keyframes text-shimmer { 0% { background-position: -220% 0; } 100% { background-position: 220% 0; } }
        @keyframes pulse { 0%,100% { transform: scale(1); opacity: 0.75; } 50% { transform: scale(1.045); opacity: 1; } }
        @keyframes rays {
          0% { transform: rotate(0deg) scale(1); opacity: 0.33; }
          50% { transform: rotate(180deg) scale(1.05); opacity: 0.58; }
          100% { transform: rotate(360deg) scale(1); opacity: 0.33; }
        }
        @keyframes flare {
          0% { transform: rotate(15deg) translateX(-30%); opacity: 0.12; }
          50% { transform: rotate(15deg) translateX(30%); opacity: 0.24; }
          100% { transform: rotate(15deg) translateX(-30%); opacity: 0.12; }
        }

        /* ---------- Layout ---------- */
        .ethereal-loading-wrapper {
          position: fixed; inset: 0;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          background: radial-gradient(1200px 800px at 50% 60%, #0f1533 0%, #0b1026 45%, #070b1a 100%);
          isolation: isolate;
          will-change: transform, opacity;
        }

        /* Aurora layers */
        .aurora-layer {
          position: absolute; inset: -20%;
          filter: blur(60px) saturate(120%); opacity: 0.72;
          animation: aurora-shift 36s linear infinite; mix-blend-mode: screen;
          pointer-events: none;
        }
        .aurora-1 {
          background: conic-gradient(from 120deg at 30% 40%, #2dd4ff22, #60a5fa44, #a78bfa55, #22d3ee33, #2dd4ff22);
        }
        .aurora-2 {
          background: conic-gradient(from -60deg at 70% 60%, #f0abfc22, #c084fc44, #60a5fa55, #93c5fd33, #f0abfc22);
          animation-duration: 48s;
        }

        /* Rays + flare */
        .rays {
          position: absolute; width: 160vmax; height: 160vmax; border-radius: 50%;
          background:
            conic-gradient(from 0deg, rgba(255,255,255,0.045), transparent 12%, rgba(255,255,255,0.045) 14%, transparent 24%, rgba(255,255,255,0.035) 26%, transparent 36%),
            radial-gradient(closest-side, rgba(255,255,255,0.08), transparent 70%);
          animation: rays 40s linear infinite; mix-blend-mode: overlay; pointer-events: none;
        }
        .flare {
          position: absolute; top: 20%; left: -40%; width: 180%; height: 8%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          filter: blur(12px); mix-blend-mode: screen; animation: flare 14s ease-in-out infinite;
          pointer-events: none;
        }

        /* Center stack */
        .center-stack { position: relative; width: min(90vw, 560px); aspect-ratio: 1 / 1; z-index: 1; }

        /* Orb + swirl */
        .orb {
          position: absolute; inset: 12%; border-radius: 50%;
          background:
            radial-gradient(circle at 50% 45%, rgba(255,255,255,0.12), rgba(255,255,255,0) 55%),
            radial-gradient(circle at 50% 55%, rgba(107,170,255,0.15), rgba(107,170,255,0) 60%),
            radial-gradient(circle at 40% 60%, rgba(167,139,250,0.12), rgba(167,139,250,0) 65%);
          filter: drop-shadow(0 0 40px rgba(120,180,255,0.25)) drop-shadow(0 0 80px rgba(120,180,255,0.15));
          animation: pulse 6s ease-in-out infinite; overflow: hidden;
        }
        .orb::after {
          content: ''; position: absolute; inset: -5%; border-radius: 50%;
          background: conic-gradient(from 0deg, rgba(230,240,255,0.06), rgba(170,200,255,0.18), rgba(180,210,255,0.08), rgba(230,240,255,0.12), rgba(200,220,255,0.06));
          mask-image: radial-gradient(circle at 50% 50%, rgba(0,0,0,0.75) 45%, rgba(0,0,0,0.35) 62%, rgba(0,0,0,0) 70%);
          animation: rotate-cw 22s linear infinite;
        }

        /* Rings */
        .ring { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
        .ring svg { overflow: visible; }
        .ring--outer { animation: rotate-cw 40s linear infinite; }
        .ring--inner { animation: rotate-ccw 28s linear infinite; }
        .ring--micro { animation: rotate-cw 16s linear infinite; }

        /* Starfield canvas */
        .starfield { position: absolute; inset: 0; z-index: 0; pointer-events: none; }

        /* Center text */
        .title {
          position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
          text-align: center; width: 100%; padding: 0 1rem; pointer-events: none; z-index: 2;
        }
        .title h2 {
          margin: 0 0 10px 0;
          font-family: "Inter", ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji";
          font-weight: 800; letter-spacing: 0.3px; line-height: 1.15;
          font-size: clamp(28px, 5vw, 46px);
          background: linear-gradient(120deg, #e9f7ff, #e9f7ff, #b9d8ff, #d6c6ff, #e9f7ff);
          background-size: 220% 100%;
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
          animation: text-shimmer 3.4s ease-in-out infinite;
          text-shadow: 0 10px 30px rgba(120, 170, 255, 0.18);
        }
        .subtitle {
          font-family: "Inter", ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans";
          font-weight: 500; font-size: clamp(14px, 2.1vw, 18px);
          color: rgba(220,232,255,0.88); text-shadow: 0 4px 18px rgba(120,170,255,0.25);
          animation: float-slow 6s ease-in-out infinite;
        }

        @media (max-width: 640px) { .center-stack { width: min(92vw, 360px); } }

        @media (prefers-reduced-motion: reduce) {
          .aurora-layer, .rays, .flare, .ring--outer, .ring--inner, .ring--micro, .title h2, .subtitle, .orb, .orb::after {
            animation: none !important;
          }
        }
      `,
        }}
      />

      <div className="ethereal-loading-wrapper" aria-busy="true" aria-live="polite">
        {/* Aurora & Rays */}
        <div className="aurora-layer aurora-1" />
        <div className="aurora-layer aurora-2" />
        <div className="rays" />
        <div className="flare" />

        {/* Starfield */}
        <canvas ref={canvasRef} className="starfield" />

        {/* Centerpiece */}
        <div className="center-stack">
          <div className="orb" />

          {/* Rotating Sigil Rings (pure SVG) */}
          <div className="ring ring--outer" aria-hidden="true">
            <svg width="100%" height="100%" viewBox="-300 -300 600 600">
              <defs>
                <radialGradient id="rg-outer" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(180,210,255,0.9)" />
                  <stop offset="60%" stopColor="rgba(160,200,255,0.35)" />
                  <stop offset="100%" stopColor="rgba(120,160,255,0)" />
                </radialGradient>
                <linearGradient id="lg-outer" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(210,235,255,0.9)" />
                  <stop offset="50%" stopColor="rgba(160,200,255,0.35)" />
                  <stop offset="100%" stopColor="rgba(210,235,255,0.9)" />
                </linearGradient>
                <filter id="glow-outer" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2.2" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Outer haze */}
              <circle r="240" fill="url(#rg-outer)" />

              {/* Outer stroke */}
              <circle
                r="220"
                fill="none"
                stroke="url(#lg-outer)"
                strokeWidth="1.6"
                strokeDasharray="6 14"
                filter="url(#glow-outer)"
              />

              {/* Decorative runes along a path */}
              <defs>
                <path id="outerPath" d="M 0 -200 A 200 200 0 1 1 -0.01 -200 Z" />
              </defs>
              <text fontSize="16" fill="rgba(230,240,255,0.75)" letterSpacing="4">
                <textPath href="#outerPath" startOffset="0%">
                  ✷  ✧  ✦  ✹  ✧  ✵  ✸  ✧  ✶  ✷  ✧  ✦  ✹  ✧  ✵  ✸  ✧  ✶
                </textPath>
              </text>
            </svg>
          </div>

          <div className="ring ring--inner" aria-hidden="true">
            <svg width="100%" height="100%" viewBox="-300 -300 600 600">
              <defs>
                <linearGradient id="lg-inner" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(245,245,255,0.95)" />
                  <stop offset="50%" stopColor="rgba(170,200,255,0.5)" />
                  <stop offset="100%" stopColor="rgba(245,245,255,0.95)" />
                </linearGradient>
                <filter id="glow-inner" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.8" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Inner detailed ring */}
              <circle
                r="165"
                fill="none"
                stroke="url(#lg-inner)"
                strokeWidth="1.2"
                strokeDasharray="4 8"
                filter="url(#glow-inner)"
              />

              {/* Micro ticks */}
              {Array.from({ length: 64 }).map((_, i) => {
                const angle = (i / 64) * Math.PI * 2;
                const x1 = Math.cos(angle) * 150;
                const y1 = Math.sin(angle) * 150;
                const x2 = Math.cos(angle) * 158;
                const y2 = Math.sin(angle) * 158;
                return (
                  <line
                    key={i}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="rgba(220,235,255,0.7)"
                    strokeWidth={i % 8 === 0 ? 1.6 : 0.8}
                    opacity={i % 2 === 0 ? 0.9 : 0.6}
                  />
                );
              })}

              {/* Spinner wedge */}
              <path
                d="M 0 -125 A 125 125 0 0 1 108.25 -62.5"
                fill="none"
                stroke="rgba(220,240,255,0.9)"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <div className="ring ring--micro" aria-hidden="true">
            <svg width="100%" height="100%" viewBox="-300 -300 600 600">
              <defs>
                <linearGradient id="lg-micro" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(235,245,255,0.95)" />
                  <stop offset="100%" stopColor="rgba(170,210,255,0.65)" />
                </linearGradient>
              </defs>

              {/* Small inner orbiting dots */}
              {Array.from({ length: 28 }).map((_, i) => {
                const angle = (i / 28) * Math.PI * 2;
                const r = 110;
                const x = Math.cos(angle) * r;
                const y = Math.sin(angle) * r;
                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r={i % 7 === 0 ? 2.2 : 1.4}
                    fill="url(#lg-micro)"
                    opacity={i % 2 === 0 ? 0.9 : 0.6}
                  />
                );
              })}

              {/* Inner ring */}
              <circle r="120" fill="none" stroke="url(#lg-micro)" strokeWidth="1.1" />
            </svg>
          </div>

          {/* Center text */}
          <div className="title">
            <h2>{loadingTexts[loadingTextIndex]}</h2>
            <div className="subtitle">Your invitation is almost ready.</div>
          </div>
        </div>

        {/* Accessible live region (offscreen) */}
        <span style={{ position: 'absolute', left: -9999, top: 'auto' }} aria-live="polite">
          {loadingTexts[loadingTextIndex]}
        </span>
      </div>
    </>
  );
};

export default BirthdayLoadingDisplay;
